import React, { useState, useEffect } from "react";
import { Button } from "react-bootstrap";

/**
 * CartButton - A button for adding products to cart with dynamic state updates
 * 
 * This component handles the UI state for products in the cart, automatically
 * updating when cart state changes propagate from Redux or other components.
 * 
 * @param {Object} props Component props 
 * @param {Function} props.addToCart Function to call when adding to cart
 * @param {Object} props.product The product to potentially add to cart
 * @param {boolean} props.inCart Whether this product is already in the cart
 * @returns {React.ReactElement} A button that changes state based on cart status
 */
const CartButton = ({ addToCart, product, inCart }) => {
  // Create a local state to track cart status, initialize with prop value
  const [isInCart, setIsInCart] = useState(inCart);
  
  // Update local state when the prop changes
  useEffect(() => {
    console.log(`CartButton for ${product.name}: inCart prop changed to ${inCart}`);
    setIsInCart(inCart);
  }, [inCart, product.name]);
  
  // Set up event listener for cart updates
  useEffect(() => {
    const handleCartUpdate = (e) => {
      // Check if this product was removed from cart
      if (e.detail && e.detail.removedItemId === product.id && isInCart) {
        console.log(`CartButton: Product ${product.name} was removed from cart`);
        setIsInCart(false);
      }
    };
    
    // Add event listener for detailed cart updates
    window.addEventListener('cart-item-removed', handleCartUpdate);
    
    // Clean up event listener on component unmount
    return () => {
      window.removeEventListener('cart-item-removed', handleCartUpdate);
    };
  }, [product.id, product.name, isInCart]);
  
  // Handle add to cart with local state update
  const handleAddToCart = (product, e) => {
    e.preventDefault();
    addToCart(product, e);
    setIsInCart(true);
  };
  
  return (
    <>
      {(product.owned || isInCart) ? (
        <Button className="preset-card-cart" variant="success">
          <i className="fa fa-check"></i>
          <span className="card-button">
            {product.owned ? ('Owned') : ('In Cart')}
          </span>
        </Button>
      ) : (
        <Button 
          onClick={(e) => handleAddToCart(product, e)} 
          className="preset-card-cart" 
          variant="primary"
        >
          <i className="fa fa-cart-plus"></i>
          <span className="price card-button">
            {product.price}
          </span>
        </Button>
      )}
    </>
  );
};

export default CartButton;
